import { has } from 'lodash';
import { IPHONE_SCREEN_TYPE, ANDROID_SCREEN_TYPE } from '../config/constants';
import { isiPhoneByScreenType, isiPadByScreenType, isiPadDevice } from './ios-device-detect';



const getPixelRatio = (screenType) => window.devicePixelRatio >= 3 && !isiPadByScreenType(screenType) ? 3 : 2;

const isAppleDevice = (screenType) => isiPhoneByScreenType(screenType) || isiPadByScreenType(screenType);

const getAppScreenVersion = (screenType, isHorizontal, isPreview) => {
  if (isiPhoneByScreenType(screenType)) {
    return `screenshot_${[ IPHONE_SCREEN_TYPE.SCREEN_6_5, IPHONE_SCREEN_TYPE.SCREEN_5_8 ].includes(screenType) ? '6_5' : '5_5'}_${isHorizontal ? 'hor' : 'ver'}_${isHorizontal && isPreview ? 'search' : 'app'}`;
  } else if (isiPadByScreenType(screenType)) {
    return `screenshot_12_9_${isHorizontal ? 'hor' : 'ver'}_small`;
  } else if (screenType === ANDROID_SCREEN_TYPE.PHONE && isPreview) {
    return `screenshot_android_${isHorizontal ? 'hor' : 'ver'}_app`;
  }

  return '';
};

const getSearchScreenVersion = (screenType, isHorizontal) =>
  isiPhoneByScreenType(screenType)
    ? `screenshot_${[ IPHONE_SCREEN_TYPE.SCREEN_6_5, IPHONE_SCREEN_TYPE.SCREEN_5_8 ].includes(screenType) ? '6_5' : '5_5'}_${isHorizontal ? 'hor' : 'ver'}_search`
    : isiPadByScreenType(screenType)
      ? `screenshot_12_9_${isHorizontal ? 'hor' : 'ver'}_small`
      : '';

export const getIconSrcSet = ({ versions, screen_type }, isSearch = false) => {
  const versionForPage = isSearch ? 'search' : 'app';

  if (isiPhoneByScreenType(screen_type)) {
    return has(versions, `icon_${versionForPage}_3x.url`) && has(versions, `icon_${versionForPage}_2x.url`) ? `${versions[`icon_${versionForPage}_2x`].url} 2x, ${versions[`icon_${versionForPage}_3x`].url} 3x` : '';
  } else if (isiPadByScreenType(screen_type)) {
    return has(versions, `icon_12_9_${versionForPage}_2x.url`) ? `${versions[`icon_12_9_${versionForPage}_2x`].url} 2x` : '';
  } else if (screen_type === ANDROID_SCREEN_TYPE.PHONE) {
    return has(versions, `icon_android_${versionForPage}_2x.url`) && has(versions, `icon_android_${versionForPage}_3x.url`) ? `${versions[`icon_android_${versionForPage}_2x`].url} 2x, ${versions[`icon_android_${versionForPage}_3x`].url} 3x` : '';
  }

  return '';
};

export const getAppScreenshotSrcSet = ({ versions, width, height, screen_type }, isPreview) => {
  const versionPath = getAppScreenVersion(screen_type, width > height, isPreview);

  if (isAppleDevice(screen_type)) {
    const srcSet = versions && has(versions, `${versionPath}_2x.url`) ? `${versions[`${versionPath}_2x`].url} 2x` : '';

    return srcSet && isiPhoneByScreenType(screen_type) && has(versions, `${versionPath}_3x.url`)
      ? `${srcSet}, ${versionPath === 'screenshot_5_5_hor_app' ? versions.screenshot_5_5_hor_search_3x.url : versions[`${versionPath}_3x`].url} 3x`
      : srcSet;
  } else if (screen_type === ANDROID_SCREEN_TYPE.PHONE) {
    return versions && has(versions, `${versionPath}_2x.url`) && has(versions, `${versionPath}_3x.url`) ? `${versions[`${versionPath}_2x`].url} 2x, ${versions[`${versionPath}_3x`].url} 3x` : '';
  }

  return '';
};

export const getSearchScreenshotSrcSet = ({ versions, width, height, screen_type }) => {
  const versionPath = getSearchScreenVersion(screen_type, width > height);
  const srcSet = versions && has(versions, `${versionPath}_2x.url`) && (isiPhoneByScreenType(window.iphoneScreenSize) || isiPadDevice())
    ? `${versions[`${versionPath}_2x`].url} 2x`
    : '';

  return srcSet && isiPhoneByScreenType(window.iphoneScreenSize) && has(versions, `${versionPath}_3x.url`)
    ? `${srcSet}, ${versions[`${versionPath}_3x`].url} 3x`
    : srcSet;
};

export const getSearchVideoPoster = ({ versions, width, height, screen_type, url }) => {
  const versionPath = getSearchScreenVersion(screen_type, width > height);
  const pixelRatio = getPixelRatio(screen_type);

  return versions && has(versions, `${versionPath}_${pixelRatio}x.url`) && (isiPhoneByScreenType(window.iphoneScreenSize) || isiPadDevice())
    ? versions[`${versionPath}_${pixelRatio}x`].url
    : url;
};

export const getAppVideoPoster = ({ versions, width, height, screen_type, url }) => {
  const isPreview = screen_type === ANDROID_SCREEN_TYPE.PHONE;
  const versionPath = getAppScreenVersion(screen_type, width > height, isPreview);
  const pixelRatio = getPixelRatio(screen_type);

  return (isAppleDevice(screen_type) || screen_type === ANDROID_SCREEN_TYPE.PHONE) && versions && has(versions, `${versionPath}_${pixelRatio}x.url`)
    ? versions[`${versionPath}_${pixelRatio}x`].url
    : screen_type === ANDROID_SCREEN_TYPE.PHONE && has(versions, `${versionPath}_${pixelRatio}x.url`)
      ? versions[`${versionPath}_${pixelRatio}x)`].url
      : url;
};

const getVideoVersion = (screenType, isHorizontal, isSearch) => {
  if (isiPhoneByScreenType(screenType)) {
    return `video_${[ IPHONE_SCREEN_TYPE.SCREEN_6_5, IPHONE_SCREEN_TYPE.SCREEN_5_8 ].includes(screenType) ? '6_5' : '5_5'}_${isSearch ? 'search' : 'app'}_${isHorizontal ? 'hor' : 'ver'}`;
  } else if (isiPadByScreenType(screenType) && isSearch) {
    return `video_12_9_small_${isHorizontal ? 'hor' : 'ver'}`;
  } else if (screenType === ANDROID_SCREEN_TYPE.PHONE) {
    return `video_android_app_${isHorizontal ? 'hor' : 'ver'}`;
  }

  return '';
};

export const getSearchVideoSrc = ({ versions, screen_type, preview: { height, width }, url }) => {
  const versionPath = getVideoVersion(screen_type, width > height, true);
  const pixelRatio = getPixelRatio(screen_type);

  return versions && has(versions, `${versionPath}_${pixelRatio}x.url`) && (isiPhoneByScreenType(window.iphoneScreenSize) || isiPadDevice())
    ? versions[`${versionPath}_${pixelRatio}x`].url
    : url;
};

export const getAppVideoSrc = ({ versions, screen_type, preview: { height, width }, url }, useVideoVersion = null) => {
  const versionPath = getVideoVersion(screen_type, width > height, false);
  const pixelRatio = useVideoVersion || getPixelRatio(screen_type);

  return [ isiPhoneByScreenType, isiPadByScreenType, (screenType) => screenType === ANDROID_SCREEN_TYPE.PHONE ].some((func) => func(screen_type) && versions && has(versions, `${versionPath}_${pixelRatio}x.url`))
    ? versions[`${versionPath}_${pixelRatio}x`].url
    : url;
};



// WEBPACK FOOTER //
// ./src/lib/srcSetService.js